import { gql } from '@apollo/client';

export const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    currentUser {
      id
      email
      roles
    }
  }
`;

// Query to fetch tax clients with their team assignments
// Note: client, principalPerson, and person only have id in the tax subgraph.
// businessName, filingType, fullName are resolved via federation from person-client subgraph.
export const TAX_CLIENTS_QUERY = gql`
  query TaxClients($filter: TaxClientFilterInput, $limit: Int, $offset: Int) {
    taxClients(filter: $filter, limit: $limit, offset: $offset) {
      items {
        client {
          id
          businessName
          filingType
        }
        principalPerson {
          id
          fullName
        }
        taxTeam {
          id
          person {
            id
            fullName
          }
          role
        }
        taxFilings {
          id
          taxYear
          stage
          subStage
        }
        isMultiBusinessTaxClient
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPrevPage
        total
      }
      coordinatorSummary {
        personId
        name
        count
      }
      advisorSummary {
        personId
        name
        count
      }
    }
  }
`;

// Query to fetch tax team candidates for assignment dropdowns
export const TAX_TEAM_CANDIDATES_QUERY = gql`
  query TaxTeamCandidates {
    taxTeamCandidates {
      id
      fullName
      email
    }
  }
`;

// Query to fetch tax filings for Tax Filing Assignment feature
export const TAX_FILINGS_QUERY = gql`
  query TaxFilings($filter: TaxFilingQueryFilterInput, $limit: Int, $offset: Int) {
    taxFilings(filter: $filter, limit: $limit, offset: $offset) {
      items {
        id
        client {
          id
          businessName
        }
        taxReturnType
        taxYear
        stage
        subStage
        deadline
        coordinator {
          id
          fullName
        }
        advisor {
          id
          fullName
        }
        preparer {
          id
          fullName
        }
        reviewer {
          id
          fullName
        }
        isMultiBusinessTaxClient
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPrevPage
        total
      }
      preparerSummary {
        personId
        name
        count
      }
      reviewerSummary {
        personId
        name
        count
      }
    }
  }
`;

// Query to fetch clients for enrollment - includes isEnrolled field
// and email for validation purposes
export const ENROLLABLE_CLIENTS_QUERY = gql`
  query EnrollableClients($filter: TaxClientFilterInput, $limit: Int, $offset: Int) {
    taxClients(filter: $filter, limit: $limit, offset: $offset) {
      items {
        client {
          id
          businessName
          filingType
        }
        principalPerson {
          id
          fullName
          email
        }
        isEnrolled
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPrevPage
      }
    }
  }
`;

// Mutation to update tax filing team assignments (preparer/reviewer)
export const UPDATE_TAX_FILING_TEAM = gql`
  mutation UpdateTaxFilingTeam($input: UpdateTaxFilingTeamInput!) {
    updateTaxFilingTeam(input: $input) {
      success
      updatedFilings {
        id
      }
      errors {
        filingId
        message
        code
        personId
        role
      }
    }
  }
`;

// Fragment for internal note fields (shared by client and filing queries)
export const INTERNAL_NOTE_FIELDS = gql`
  fragment InternalNoteFields on InternalNote {
    id
    body
    createdOn
    flagged
    author {
      id
      firstName
      lastName
      fullName
    }
  }
`;

// Query to fetch a single tax client with details for Client Details page
// Uses Bench-side data (TaxClient) for client info and team assignments
export const TAX_CLIENT_DETAILS_QUERY = gql`
  query TaxClientDetails($clientId: ID!) {
    taxClient(clientId: $clientId) {
      client {
        id
        businessName
      }
      principalPerson {
        id
        fullName
        email
        phone
      }
      taxTeam {
        id
        person {
          id
          fullName
        }
        role
        taxFilingId
      }
      taxFilings {
        id
        taxYear
        filingType
        stage
        subStage
      }
      advisory
      isMultiBusinessTaxClient
      relatedTaxBusinesses {
        id
        businessName
      }
      internalNotes {
        ...InternalNoteFields
      }
    }
  }
  ${INTERNAL_NOTE_FIELDS}
`;

// Query to fetch a tax filing with details for Filing Details page
export const TAX_FILING_DETAILS_QUERY = gql`
  query TaxFilingDetails($clientId: ID!, $filingId: ID!) {
    taxFilingForClient(clientId: $clientId, filingId: $filingId) {
      id
      taxYear
      filingType
      stage
      subStage
      payTaxesWhenFiling
      company {
        id
        displayName
      }
      approvalData {
        totalFederalIncomeCents
        totalFederalDeductionsCents
        amountsPerJurisdictionCents {
          abbreviation
          amount
          type
        }
      }
      programs {
        id
        programType
        stage
        jurisdiction {
          name
          abbreviation
        }
      }
      questionnaires {
        id
        name
        status
        questionnaire {
          cmsGroupId
        }
        answers {
          questionId
          value {
            textValue
            numberValue
            booleanValue
            selectedOptions
          }
        }
      }
      achPaymentInfo {
        id
        clientId
        financialInstitutionName
        routingTransitNumber
        accountNumber
        accountType
      }
      internalNotes {
        ...InternalNoteFields
      }
    }
  }
  ${INTERNAL_NOTE_FIELDS}
`;

// Query to fetch tax documents for a client using bookkeeping subgraph
// Uses the paginated documentsByType query with TAX filter
export const TAX_DOCUMENTS_QUERY = gql`
  query TaxDocumentsForClient($clientId: ID!, $limit: Int, $offset: Int, $search: String) {
    documentsByType(
      input: {
        clientId: $clientId
        documentType: TAX
        limit: $limit
        offset: $offset
        search: $search
      }
    ) {
      items {
        id
        name
        vendorName
        date
        viewed
        file {
          id
          displayName
          originalName
        }
      }
      pageInfo {
        total
        hasNextPage
        hasPrevPage
      }
    }
  }
`;

// Lightweight query to poll only messaging stats for dashboard filings.
// Apollo's normalized cache merges updated messagingStats into existing TaxFilingQueryItem objects by id.
export const DASHBOARD_MESSAGING_STATS_QUERY = gql`
  query DashboardMessagingStats($filter: TaxFilingQueryFilterInput, $limit: Int, $offset: Int) {
    taxFilings(filter: $filter, limit: $limit, offset: $offset) {
      items {
        id
        messagingStats {
          unreadCount
          lastStaffContactDate
          oldestUnreadClientMessageDate
        }
      }
    }
  }
`;

// Query to fetch job history (sibling tasks) and notes for the task slider
export const JOB_HISTORY_QUERY = gql`
  query JobHistory($jobId: ID!) {
    job(id: $jobId) {
      id
      tasks {
        id
        type
        status
        resolution
        createdOn
        assignedOn
        resolvedOn
        assigneeType
        assignedPerson {
          id
          fullName
        }
      }
      taskNotes {
        id
        body
        createdBy {
          id
          fullName
        }
        createdOn
        task {
          id
          type
        }
      }
    }
  }
`;

// Query to fetch the logged-in person (for task assignment)
export const LOGGED_IN_PERSON_QUERY = gql`
  query GetLoggedInPerson {
    loggedInPerson {
      id
      fullName
    }
  }
`;

// Query to fetch open tax return tasks for a client
// Uses the tasks query from the v2 subgraph with inline fragment for job parameters
export const TAX_RETURN_TASKS_QUERY = gql`
  query TaxReturnTasks($filterBy: TasksFilterInput) {
    tasks(filterBy: $filterBy) {
      tasks {
        id
        type
        status
        assigneeType
        assignedPerson {
          id
          fullName
        }
        createdOn
        job {
          id
          type
          parameters {
            ... on ApproveTaxReturnJobParameters {
              year
              taxReturnId
            }
          }
        }
      }
      pageInfo {
        total
        hasNextPage
        hasPrevPage
      }
    }
  }
`;

// Lightweight query to fetch all filings for an assignee (id, clientId, taxYear, stage only).
// Used to compute prior-year blocking status client-side across paginated views.
export const PRIOR_YEAR_BLOCKING_QUERY = gql`
  query PriorYearBlockingFilings($filter: TaxFilingQueryFilterInput) {
    taxFilings(filter: $filter) {
      items {
        id
        client {
          id
        }
        taxYear
        filingType: taxReturnType
        stage
      }
      totalCount
    }
  }
`;

// Query to fetch tax filing package documents
export const TAX_FILING_DOCUMENTS_QUERY = gql`
  query TaxFilingDocuments($clientId: ID!, $filingId: ID!) {
    taxFilingDocuments(clientId: $clientId, filingId: $filingId) {
      id
      version
      versionTag
      taxFiling {
        id
        taxYear
        filingType
      }
      document {
        id
        name
        file {
          id
          displayName
          originalName
        }
        vendorName
        type
        taxDocumentType
      }
    }
  }
`;

// Query to fetch tax filings for the Dashboard
export const DASHBOARD_FILINGS_QUERY = gql`
  query DashboardFilings($filter: TaxFilingQueryFilterInput, $limit: Int, $offset: Int) {
    taxFilings(filter: $filter, limit: $limit, offset: $offset) {
      items {
        id
        client {
          id
          businessName
          inboxState
          principalPerson {
            id
            fullName
          }
        }
        taxReturnType
        taxYear
        stage
        subStage
        deadline
        yefComplete
        advisory
        messagingStats {
          unreadCount
          lastStaffContactDate
          oldestUnreadClientMessageDate
        }
        coordinator {
          id
          fullName
        }
        advisor {
          id
          fullName
        }
        preparer {
          id
          fullName
        }
        reviewer {
          id
          fullName
        }
        isMultiBusinessTaxClient
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPrevPage
      }
    }
  }
`;
